import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { AREAS, SERVICES, BUSINESS, PRICING_EXAMPLES, REVIEWS, FAQS } from "@/lib/business";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, MessageCircle, Calendar, ShieldCheck, PoundSterling, Timer, CheckCircle2 } from "lucide-react";
import RatingPill from "@/components/site/RatingPill";
import ServiceCard from "@/components/site/ServiceCard";
import ReviewCard from "@/components/site/ReviewCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function AreaPage() {
  const { slug } = useParams();
  const area = AREAS.find(a => a.slug === slug);

  useEffect(() => {
    if (!area) return;
    document.title = `Phone, iPad, MacBook & Laptop Repair in ${area.name} | iCrack Solutions`;
    const setMeta = (name, content, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const desc = `Fast, trusted phone, tablet, Mac and laptop repair in ${area.name}, ${area.region}. Walk-in at 94 Market Street, Eastleigh or book a mobile call-out to your home or office. £120/hr call-out labour + parts. 4.9★ (294 Google reviews).`;
    setMeta("description", desc);
    setMeta("og:title", document.title, "property");
    setMeta("og:description", desc, "property");
    setMeta("twitter:title", document.title);
    setMeta("twitter:description", desc);

    const data = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: `iCrack Solutions – Phone & Computer Repair (${area.name})`,
      areaServed: { "@type": "City", name: area.name },
      address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS.address.line1,
        addressLocality: BUSINESS.address.city,
        addressRegion: BUSINESS.address.region,
        postalCode: BUSINESS.address.postcode,
        addressCountry: "GB",
      },
      telephone: BUSINESS.phones.map(p => `+${p.tel.replace(/\D/g,"")}`),
      priceRange: "££",
      aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "294" },
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-area-schema", "true");
    script.text = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      const existing = document.querySelector('script[data-area-schema="true"]');
      if (existing) existing.remove();
    };
  }, [area]);

  if (!area) return <Navigate to="/areas" replace />;

  return (
    <div>
      {/* HERO — no device photo; brand gradient card with area name */}
      <section className="relative overflow-hidden hero-wash noise-overlay" data-testid={`area-page-${area.slug}`}>
        <div className="container-x pt-10 pb-14 sm:pt-14 sm:pb-16">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">{area.region}</p>
              <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight">
                Phone, iPad, Mac &amp; Laptop Repair in <span style={{ color: "var(--icrack-magenta)" }}>{area.name}</span>
              </h1>
              <p className="mt-3 text-slate-600 max-w-xl">{area.blurb}</p>

              <div className="mt-6 flex flex-wrap gap-3 items-center">
                <Link to="/book"><Button className="h-11 rounded-xl btn-primary-magenta"><Calendar className="h-4 w-4 mr-2"/>Book Repair</Button></Link>
                <a href={`tel:${BUSINESS.phones[0].tel}`}><Button variant="outline" className="h-11 rounded-xl border-slate-200"><Phone className="h-4 w-4 mr-2" style={{color:"var(--icrack-magenta)"}}/>{BUSINESS.phones[0].display}</Button></a>
                <a href={BUSINESS.whatsapp.href} target="_blank" rel="noreferrer"><Button variant="outline" className="h-11 rounded-xl border-slate-200"><MessageCircle className="h-4 w-4 mr-2 text-green-600"/>WhatsApp</Button></a>
              </div>

              <div className="mt-5"><RatingPill /></div>
            </div>

            {/* Locality visual card — map + area badges (no device photo) */}
            <div className="relative" data-testid="area-hero-visual">
              <div className="absolute -inset-2 rounded-[2rem] bg-gradient-to-br from-pink-100 via-white to-white blur-2xl opacity-70" aria-hidden />
              <div className="relative rounded-3xl border border-pink-100 shadow-[0_18px_60px_rgba(229,0,125,0.18)] overflow-hidden bg-white">
                <div className="aspect-[4/3] relative">
                  <iframe
                    title={`${area.name} map`}
                    src={`https://www.google.com/maps?q=${encodeURIComponent(area.name + ', ' + area.region + ', UK')}&output=embed`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    data-testid="area-map"
                  />
                </div>
                <div className="p-5 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" style={{ color: "var(--icrack-magenta)" }} />
                    <span className="font-semibold text-slate-900">{area.name}</span>
                    <span className="text-slate-500 text-sm">• {area.region}</span>
                    <span className="ml-auto text-slate-500 text-sm">{area.postcodePrefix}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="section-pad">
        <div className="container-x grid gap-6 md:grid-cols-3">
          {[
            { icon: PoundSterling, title: "No call-out charge", body: `Free technician visit anywhere in ${area.name} and nearby (${area.postcodePrefix}). £120/hr labour + parts on-site.` },
            { icon: Timer, title: "Fast turnaround", body: "Most on-site repairs finished in 30\u201390 minutes." },
            { icon: ShieldCheck, title: "No fix, no fee", body: "3-month warranty on parts + labour as standard." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "var(--icrack-magenta-soft)" }}>
                <Icon className="h-5 w-5" style={{ color: "var(--icrack-magenta-deep)" }} />
              </span>
              <h3 className="mt-4 font-semibold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES IN AREA */}
      <section className="section-pad bg-slate-50">
        <div className="container-x">
          <p className="text-sm font-semibold text-[color:var(--icrack-magenta-deep)] uppercase tracking-wider">Services in {area.name}</p>
          <h2 className="mt-1 font-display text-3xl font-bold">Repairs we do for {area.name} customers</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.slice(0, 6).map(s => <ServiceCard key={s.slug} service={s} />)}
          </div>
          <div className="mt-6">
            <Link to="/services" className="link-magenta font-semibold text-sm">See full service list →</Link>
          </div>
        </div>
      </section>

      {/* LANDMARKS */}
      <section className="section-pad">
        <div className="container-x grid gap-8 md:grid-cols-2 md:items-center">
          <div>
            <h2 className="font-display text-3xl font-bold">Neighbourhoods we cover in {area.name}</h2>
            <p className="mt-2 text-slate-600">If you're near any of the areas below, book a call-out or stop by the workshop.</p>
            <div className="mt-5 flex flex-wrap gap-2">
              {area.landmarks.map(l => (
                <span key={l} className="inline-flex items-center gap-1.5 rounded-full border border-pink-200 bg-pink-50 px-3 py-1 text-sm text-slate-800"><MapPin className="h-3.5 w-3.5" style={{ color: "var(--icrack-magenta)" }} />{l}</span>
              ))}
            </div>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5"/>Same-day slots when available</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5"/>Genuine-quality parts &amp; sealed batteries</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5"/>Free diagnostic before we start</li>
            </ul>
          </div>
          <div className="rounded-3xl border border-slate-200 overflow-hidden bg-white">
            <div className="p-6">
              <h3 className="font-semibold text-slate-900">Pricing for {area.name}</h3>
              <p className="text-sm text-slate-600 mt-1">In-store repairs are fixed-price. Call-out visits are billed at a flat labour rate.</p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-100 p-3">
                  <p className="text-xs text-slate-500">In-store</p>
                  <p className="mt-1 font-semibold">Fixed price</p>
                  <p className="text-xs text-slate-500">from £29</p>
                </div>
                <div className="rounded-xl border border-pink-200 bg-pink-50 p-3">
                  <p className="text-xs" style={{color:"var(--icrack-magenta-deep)"}}>Call-out</p>
                  <p className="mt-1 font-semibold">£{BUSINESS.callOutLabourRate}/hr</p>
                  <p className="text-xs text-slate-500">+ parts • no visit fee</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 border-t border-slate-100">
              {PRICING_EXAMPLES.slice(0, 4).map((p) => (
                <div key={p.title} className="p-4 border-r border-b border-slate-100 last:border-r-0 [&:nth-child(2n)]:border-r-0 [&:nth-last-child(-n+2)]:border-b-0">
                  <p className="text-xs text-slate-500">{p.title}</p>
                  <p className="mt-1 font-semibold">{p.from === 0 ? "Free" : `from £${p.from}`}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="section-pad bg-slate-50">
        <div className="container-x">
          <h2 className="font-display text-3xl font-bold">What our customers say</h2>
          <p className="mt-2 text-slate-600">Real reviews from our <a className="link-magenta" href={BUSINESS.googleBusinessUrl} target="_blank" rel="noreferrer">Google Business Profile</a>.</p>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {REVIEWS.map((r, i) => <ReviewCard key={i} review={r} index={i} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-pad">
        <div className="container-x">
          <div className="rounded-3xl border border-pink-200 p-6 sm:p-10" style={{ background: "var(--icrack-magenta-soft)" }}>
            <div className="grid gap-6 md:grid-cols-2 md:items-center">
              <div>
                <h2 className="font-display text-3xl font-bold">Ready to book in {area.name}?</h2>
                <p className="mt-2 text-slate-700">Grab a slot online or call us direct. We'll confirm within minutes.</p>
              </div>
              <div className="flex flex-wrap gap-3 md:justify-end">
                <Link to="/book"><Button className="h-11 rounded-xl btn-primary-magenta"><Calendar className="h-4 w-4 mr-2"/>Book now</Button></Link>
                <a href={`tel:${BUSINESS.phones[0].tel}`}><Button variant="outline" className="h-11 rounded-xl border-slate-200 bg-white"><Phone className="h-4 w-4 mr-2" style={{color:"var(--icrack-magenta)"}}/>{BUSINESS.phones[0].display}</Button></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl font-bold">FAQs — {area.name}</h2>
          <Accordion type="single" collapsible className="mt-6">
            {FAQS.flatMap(g => g.items).slice(0, 5).map((f, i) => (
              <AccordionItem value={`faq-${i}`} key={i}>
                <AccordionTrigger className="text-left text-base font-semibold">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-700 text-sm leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
